import { ReactNode, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import { CustomFilterConfig } from '@alltrails/denali/components/FilterBar';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import ExploreServiceSort from '../../types/ExploreServiceSort';
import { exploreServiceSortDisplayOrder } from '../../utils/constants';
import getExploreServiceSortLabel from '../../utils/getExploreServiceSortLabel';
import styles from './SortDropdown.module.scss';

type UseSortDropdownFilterConfigProps = {
  additionalContent?: ReactNode;
  customSortOptions?: { label: ReactNode; value: string }[];
  customSortValue?: string;
  exploreServiceSort: ExploreServiceSort;
  onCustomSortChange?: (updatedSort: string) => void;
  onSortChange: (updatedSort: ExploreServiceSort) => void;
};

const useSortDropdownFilterConfig = ({
  additionalContent,
  customSortOptions,
  customSortValue,
  exploreServiceSort,
  onCustomSortChange,
  onSortChange
}: UseSortDropdownFilterConfigProps): CustomFilterConfig => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const isMonthSort = exploreServiceSort && exploreServiceSort.startsWith('month_');
  // Map month sorts to 'seasonal' for display
  const displaySort = isMonthSort ? 'seasonal' : exploreServiceSort;

  const selectedLabel = useMemo(() => {
    if (customSortOptions) {
      const customSortOption = customSortOptions.find(option => option.value === customSortValue);
      return customSortOption?.label ?? '';
    }
    return getExploreServiceSortLabel(intl, displaySort);
  }, [customSortOptions, customSortValue, intl, displaySort]);

  const sortOptions = useMemo(() => {
    if (customSortOptions && onCustomSortChange) {
      return customSortOptions.map(({ label, value }) => ({
        key: value,
        title: label,
        selected: value === customSortValue,
        onClick: () => {
          onCustomSortChange(value);
          setIsOpen(false);
        },
        testId: value
      }));
    }

    return exploreServiceSortDisplayOrder.map(sort => ({
      key: sort,
      title: getExploreServiceSortLabel(intl, sort),
      selected: sort === displaySort,
      onClick: () => {
        onSortChange(sort);
        setIsOpen(false);
      },
      testId: sort
    }));
  }, [customSortOptions, onCustomSortChange, customSortValue, intl, displaySort, onSortChange]);

  const dropdownContent = (
    <div className={styles.container}>
      <div>
        <div className={styles.label}>{intl.formatMessage({ defaultMessage: 'Sort' })}</div>
        <DropdownMenu
          anchor={
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label={intl.formatMessage({ defaultMessage: 'Select sort option' })}
              className={styles.dropdownButton}
            >
              <span>{selectedLabel}</span>
              <ChevronSm orientation={isOpen ? 'up' : 'down'} size="sm" />
            </button>
          }
          className={styles.dropdownContainer}
          isOpen={isOpen}
          onCloseRequest={() => setIsOpen(false)}
          options={sortOptions}
          placement="bottom-start"
          textVariant="text200"
        />
      </div>
      {additionalContent && <div className={styles.additionalContent}>{additionalContent}</div>}
    </div>
  );

  return {
    type: 'custom-dropdown',
    id: 'sort',
    filterChip: {
      onClear: () => {
        // Clear would reset to default sort if needed
      },
      selectedItems: [selectedLabel],
      testId: 'sort-filter-chip',
      unselectedText: intl.formatMessage({ defaultMessage: 'Sort' })
    },
    content: dropdownContent
  };
};

export default useSortDropdownFilterConfig;
